type RelatedProductGridDisplayMode = 'mobile' | 'desktop';

interface RelatedProductGridDisplayCount {
    desktop: number;
    mobile: number;
}

interface RelatedProductGridItem {
    id: number | string;
}

export const DEFAULT_RELATED_PRODUCT_GRID_DISPLAY_COUNT: RelatedProductGridDisplayCount =
    {
        desktop: 10,
        mobile: 4,
    };

/**
 * Returns the visible related-product ids for the current display mode.
 */
export const getVisibleRelatedProductIds = <T extends RelatedProductGridItem>(
    products: T[],
    appDisplayMode: RelatedProductGridDisplayMode,
    displayCount: RelatedProductGridDisplayCount = DEFAULT_RELATED_PRODUCT_GRID_DISPLAY_COUNT,
): string[] => {
    return products
        .slice(0, displayCount[appDisplayMode])
        .map(({ id }) => String(id));
};

/**
 * Checks whether a related-product grid should render a "View more" CTA.
 */
export const shouldShowRelatedProductGridCta = (
    totalCount: number,
    appDisplayMode: RelatedProductGridDisplayMode,
    displayCount: RelatedProductGridDisplayCount = DEFAULT_RELATED_PRODUCT_GRID_DISPLAY_COUNT,
): boolean => {
    return totalCount > displayCount[appDisplayMode];
};

/**
 * Checks whether two related-product grids would show the same visible products.
 */
export const haveSameVisibleRelatedProducts = <
    T extends RelatedProductGridItem,
>(
    leftProducts: T[],
    rightProducts: T[],
    appDisplayMode: RelatedProductGridDisplayMode,
    displayCount: RelatedProductGridDisplayCount = DEFAULT_RELATED_PRODUCT_GRID_DISPLAY_COUNT,
): boolean => {
    const leftVisibleIds = getVisibleRelatedProductIds(
        leftProducts,
        appDisplayMode,
        displayCount,
    );
    const rightVisibleIds = getVisibleRelatedProductIds(
        rightProducts,
        appDisplayMode,
        displayCount,
    );

    if (leftVisibleIds.length !== rightVisibleIds.length) {
        return false;
    }

    const leftVisibleIdSet = new Set(leftVisibleIds);

    return rightVisibleIds.every((id) => leftVisibleIdSet.has(id));
};

/**
 * Checks whether a related-product grid duplicates any earlier visible grid.
 */
export const isDuplicateVisibleRelatedProductGrid = <
    T extends RelatedProductGridItem,
>(
    candidateProducts: T[],
    earlierProductGrids: T[][],
    appDisplayMode: RelatedProductGridDisplayMode,
    displayCount: RelatedProductGridDisplayCount = DEFAULT_RELATED_PRODUCT_GRID_DISPLAY_COUNT,
): boolean => {
    if (candidateProducts.length === 0) {
        return false;
    }

    return earlierProductGrids.some((products) =>
        haveSameVisibleRelatedProducts(
            candidateProducts,
            products,
            appDisplayMode,
            displayCount,
        ),
    );
};
